import { useState, type FormEvent } from "react";
import { useMeContext } from "../../MeApp";
import { useMeAuth } from "../../auth/MeAuthContext";
import { useSelectPartner } from "../../meHooks";
import { StepShell, StepHeading, RadioCard } from "@codel/dova-ds";
import { Button } from "../components/Button";
import type { StepProps } from "../flowConfig";

/**
 * S2 — "Who's setting up?" (self-select). The opener arrived on a couple-scoped
 * token (no person bound yet), so BOTH partners are shown ENABLED and they pick
 * themselves. Selecting one calls `POST /me/select-partner`, which mints the
 * person-bound token; storing it flips the session to post-bind and the rest of
 * onboarding (S3+) runs person-scoped, unchanged.
 *
 * (This replaces the prior JWT-fixed "Confirm it's you" card — production no
 * longer pre-binds a partner from a per-person magic link; the link is one
 * shared couple link and identity is self-selected here.)
 */
export function S2PartnerPicker({
  onNext,
  onBack,
  onJump,
  currentStepIndex,
  totalSteps,
}: StepProps) {
  const { couple } = useMeContext();
  const { setToken } = useMeAuth();
  const selectPartner = useSelectPartner();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  if (!couple) return null; // MeShell shows the loader

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!selectedId || selectPartner.isPending) return;
    try {
      const personToken = await selectPartner.mutateAsync(selectedId);
      // Overwrite the couple token with the person-bound token → post-bind.
      setToken(personToken);
      onNext();
    } catch (err) {
      // selectPartner failures surface inline below (selectPartner.isError) and
      // we stay on the picker so they can retry. Log anything else (e.g. a
      // setToken/storage failure on the success path) so it isn't fully silent.
      console.warn("[me] selectPartner rejected:", err);
    }
  };

  return (
    <StepShell
      showBack
      onBack={onBack}
      currentStep={currentStepIndex}
      totalSteps={totalSteps}
      onSubmit={handleSubmit}
      footer={
        <Button type="submit" disabled={!selectedId || selectPartner.isPending}>
          Continue
        </Button>
      }
    >
      <StepHeading title="Who's setting up?" />

      <div className="flex flex-col gap-4">
        {couple.partners.map((p) => (
          <RadioCard
            key={p.id}
            name="who-is-setting-up"
            value={p.id}
            label={p.firstName}
            description={p.lastName}
            // Circles, matching the welcome screen's pair. These two rows are
            // the same two people that screen just introduced, so the mark that
            // stood for them there should be the mark they are picked by here.
            avatarShape="circle"
            avatar={
              p.avatarUrl ? (
                <img
                  src={p.avatarUrl}
                  alt=""
                  className="h-full w-full object-cover"
                />
              ) : (
                (p.firstName[0] ?? "").toUpperCase()
              )
            }
            checked={selectedId === p.id}
            onChange={() => setSelectedId(p.id)}
          />
        ))}
        <button
          type="button"
          onClick={() => onJump("neither")}
          className="w-full rounded-[var(--radius-lg)] border border-dashed border-[var(--border)] py-4 px-4 text-sm text-[var(--text-secondary)] hover:border-[var(--text-tertiary)] hover:text-[var(--text-primary)] transition-colors"
        >
          Neither of these is me
        </button>
        {selectPartner.isError && (
          <p
            role="alert"
            className="text-sm text-danger text-center"
          >
            That selection didn’t save. Try again.
          </p>
        )}
      </div>
    </StepShell>
  );
}
